import { buildFormValueMsg, buildFormItemMsg, buildFormLoopMsg, buildFormFlowMsg, buildPersistMsg, buildContractMsg, parseContractReplyMsg, parseFormValueMsg, parseFormItemMsg, parseFormLoopMsg, parseFormFlowMsg } from './messageSchema';

export function createSyncBridge({ flowStore, flowEvents, uiStore, sessionStore, client }) {
    const disposers = [];

    const topic = (base) => {
        const s = sessionStore.getState().topicsSuffix;
        return s ? `${base}-${s}` : base;
    };

    // Echo suppression: track recently self-emitted topic+body keys.
    // The InMemoryBroker is synchronous, so entries expire after one microtask.
    const recentlySelfEmitted = new Set();
    const remember = (key) => {
        recentlySelfEmitted.add(key);
        queueMicrotask(() => recentlySelfEmitted.delete(key));
    };

    const wrappedPublish = (topicName, body) => {
        remember(topicName + '|' + JSON.stringify(body));
        try { client.publish(topicName, body); } catch (e) { /* connection gone */ }
    };

    const persistSnapshot = () => {
        const sess = sessionStore.getState();
        if (!sess.sessionCreated || !sess.sessionIdPublic || !sess.sessionIdPrivate) return;
        const snapshot = flowStore.getState();
        try { client.publish('/queue/form-persist', buildPersistMsg({
            sessionIdPublic: sess.sessionIdPublic,
            sessionIdPrivate: sess.sessionIdPrivate,
            snapshot,
            action: 'save',
        })); } catch (e) { /* connection gone */ }
    };

    const unsubEvents = flowEvents.subscribe((event) => {
        if (event.source !== 'local') return;
        // Observers may navigate locally for their own viewing, but must never broadcast
        // back into the session — otherwise the editor's view would follow the observer.
        if (sessionStore.getState().mode === 'observer') return;
        switch (event.type) {
            case 'attributeChanged':
                wrappedPublish(topic('/topic/form-value'), buildFormValueMsg(event));
                break;
            case 'itemChanged':
                wrappedPublish(topic('/topic/form-item'), buildFormItemMsg(event));
                persistSnapshot();
                break;
            case 'flowChanged':
                wrappedPublish(topic('/topic/form-flow'), buildFormFlowMsg(event));
                // A flow change resets the snapshot; persist it so a reload of the
                // session does not come back on the previous flow.
                persistSnapshot();
                break;
            case 'loopUpdated':
                wrappedPublish(topic('/topic/form-loop'), buildFormLoopMsg(event));
                break;
            default: break;
        }
    });
    disposers.push(unsubEvents);

    // Inbound: subscribe to broadcast topics and apply incoming messages to the local store.
    // Both editor and observer subscribe; echo suppression (via recentlySelfEmitted) prevents
    // the publisher from re-applying its own broadcasts.
    //
    // Subscription is deferred until the STOMP client is connected AND a session has been
    // joined (so topicsSuffix is known). It is reinstalled whenever the suffix changes
    // (session swap) or the connection drops and recovers.
    let installedSubs = [];
    let installedSuffix = null;

    const teardownInbound = () => {
        for (const u of installedSubs) { try { u(); } catch (e) {} }
        installedSubs = [];
        installedSuffix = null;
    };

    const setupInbound = () => {
        const suffix = sessionStore.getState().topicsSuffix;
        if (!client.connected || !suffix) return;
        if (installedSuffix === suffix) return;
        teardownInbound();

        const safeSub = (t, handler) => {
            try {
                return client.subscribe(t, (body) => {
                    if (recentlySelfEmitted.has(t + '|' + JSON.stringify(body))) return;
                    handler(body);
                });
            } catch (e) { return () => {}; }
        };
        installedSubs.push(safeSub(topic('/topic/form-value'), (body) => {
            const { itemId, attribute, fieldIndex, value } = parseFormValueMsg(body);
            if (attribute === 'applyToAll') {
                flowStore.getState().setApplyToAll(itemId, value, { remote: true });
            } else {
                flowStore.getState().setAttribute(itemId, attribute, fieldIndex, value, { remote: true });
            }
        }));
        installedSubs.push(safeSub(topic('/topic/form-item'), (body) => {
            const parsed = parseFormItemMsg(body);
            flowStore.getState().jumpTo({
                itemId: parsed.itemId,
                path: parsed.path,
                loop: parsed.pathItem?.loop,
            }, { remote: true });
        }));
        installedSubs.push(safeSub(topic('/topic/form-flow'), (body) => {
            const { flowId } = parseFormFlowMsg(body);
            try {
                flowStore.getState().changeFlow(flowId, { remote: true });
            } catch (e) {
                // The sender runs a flow this client does not know about.
                uiStore.getState().setGlobalError(e);
            }
        }));
        installedSubs.push(safeSub(topic('/topic/form-loop'), (body) => {
            const { loopName, loop } = parseFormLoopMsg(body);
            // Apply the loop snapshot directly — overlay only, no engine re-run.
            flowStore.setState((s) => ({
                loops: { ...s.loops, [loopName]: loop },
            }));
        }));
        installedSuffix = suffix;
    };

    const unsubConnect = client.onConnect(setupInbound);
    // Broker drops subscriptions on disconnect; clear our tracking so reconnect re-installs.
    const unsubDisconnect = client.onDisconnect(() => {
        installedSubs = [];
        installedSuffix = null;
    });
    const unsubSessionWatch = sessionStore.subscribe((s, prev) => {
        if (s.topicsSuffix === prev.topicsSuffix) return;
        if (s.topicsSuffix) setupInbound();
        else teardownInbound();
    });
    // Cover the case where bridge is created after both conditions are already met
    // (e.g. tests that connect + sessionJoined before instantiating the bridge).
    setupInbound();

    disposers.push(() => teardownInbound());
    disposers.push(unsubConnect);
    disposers.push(unsubDisconnect);
    disposers.push(unsubSessionWatch);

    const unsubUnhandled = client.onUnhandledMessage((raw) => {
        const sub = raw.headers && raw.headers.subscription;
        if (sub === '/temp-queue/contract_created') {
            let body;
            try { body = JSON.parse(raw.body); } catch (e) { return; }
            uiStore.getState().setContract(parseContractReplyMsg(body));
        } else if (sub === '/temp-queue/newsession' || sub === '/temp-queue/new_session' || sub === '/temp-queue/join_session') {
            let body;
            try { body = JSON.parse(raw.body); } catch (e) { return; }
            sessionStore.getState().sessionJoined(body);
            if (body.state && body.state.currentFlowId !== undefined) {
                try {
                    flowStore.getState().restoreSnapshot(body.state, { remote: true });
                } catch (e) {
                    uiStore.getState().setGlobalError(e);
                }
            }
        }
    });
    disposers.push(unsubUnhandled);

    return {
        dispose() { for (const d of disposers) { try { d(); } catch (e) {} } },
        createSession() {
            try {
                client.publish('/queue/form-create', '', { 'reply-to': '/temp-queue/newsession' });
            } catch (e) {}
        },
        joinSession({ sessionIdPublic, sessionIdPrivate }) {
            try {
                client.publish(
                    '/queue/form-join',
                    {
                        session_id_public: sessionIdPublic,
                        // The backend splits session_id_private on '#'. Callers
                        // supply it either way: the join modal passes the
                        // combined string the user pasted, while the token
                        // refresh passes the two ids separately. Compose only
                        // when it is not already combined -- doing it blindly
                        // produced 'undefined#<id>' from the modal, which the
                        // listener could not resolve.
                        ...(sessionIdPrivate
                            ? {
                                session_id_private: sessionIdPrivate.includes('#')
                                    ? sessionIdPrivate
                                    : `${sessionIdPublic}#${sessionIdPrivate}`,
                            }
                            : {}),
                    },
                    { 'reply-to': '/temp-queue/join_session' },
                );
            } catch (e) {}
        },
        generateContract() {
            const sess = sessionStore.getState();
            const snapshot = flowStore.getState();
            const msg = buildContractMsg({ sessionIdPublic: sess.sessionIdPublic, snapshot });
            try { client.publish('/queue/contract', msg, { 'reply-to': '/temp-queue/contract_created' }); } catch (e) {}
        },
        deleteSession() {
            const sess = sessionStore.getState();
            if (!sess.sessionIdPublic || !sess.sessionIdPrivate) return;
            try {
                client.publish('/queue/form-persist', {
                    session_id_private: `${sess.sessionIdPublic}#${sess.sessionIdPrivate}`,
                    action: 'delete',
                });
            } catch (e) {}
            sessionStore.getState().sessionEnded();
            flowStore.getState().reset();
        },
    };
}
